import { ref } from "vue";
import { AREA_CACHE } from "@/api/const/area";
import { apiGetV2 } from "@/api/index";

export interface AreaNode {
  id: string | number;
  name: string;
  children?: AreaNode[];
  [key: string]: unknown;
}

export interface AreaSelectOption {
  label: string;
  value: string | number;
  raw: AreaNode;
  disabled?: boolean;
}

const areaTree = ref<AreaNode[]>([]);
const loading = ref(false);
let loadPromise: Promise<AreaNode[]> | null = null;

export const normalizeSearch = (str: string) =>
  (str || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/đ/g, "d")
    .replace(/Đ/g, "d");

export const filterAreaOption = (input: string, option: any) =>
  normalizeSearch(String(option?.label ?? "")).includes(normalizeSearch(input));

export const mapAreaOptions = (list: AreaNode[] = []): AreaSelectOption[] =>
  (list || []).map((area) => ({
    label: area.name,
    value: area.id,
    raw: area,
  }));

export const findAreaById = (
  list: AreaNode[] = [],
  id: string | number | null | undefined
): AreaNode | undefined => {
  if (id === null || id === undefined || id === "") return undefined;
  return (list || []).find((item) => String(item.id) === String(id));
};

export async function loadAreaTree(force = false): Promise<AreaNode[]> {
  if (!force && areaTree.value.length) {
    return areaTree.value;
  }

  if (!force && loadPromise) {
    return loadPromise;
  }

  loading.value = true;
  loadPromise = (async () => {
    try {
      const res = await apiGetV2(AREA_CACHE + "area-client-list");
      const raw = res?.data?.data;
      const parsed = typeof raw === "string" ? JSON.parse(raw || "[]") : raw || [];
      areaTree.value = Array.isArray(parsed) ? parsed : [];
      return areaTree.value;
    } catch {
      areaTree.value = [];
      return areaTree.value;
    } finally {
      loading.value = false;
      loadPromise = null;
    }
  })();

  return loadPromise;
}

export function useArea() {
  return {
    areaTree,
    loading,
    loadAreaTree,
    mapAreaOptions,
    findAreaById,
    filterAreaOption,
    normalizeSearch,
  };
}
